import { c } from 'ttag';

import { AccountUsersQuery } from '../../graphql/__generated__/types';

export type AccountUserRow = NonNullable<NonNullable<AccountUsersQuery['accountUsers']>['items']>[number];

/**
 * The accounts, as « Verlaine Energie +2 ». The surplus counts the accounts the actor can see,
 * not the ones the person has: `memberships` is bounded by the actor's perimeter, so two operators
 * with different portfolios read different surpluses for the same person.
 */
export const accountsCell = (
    user: AccountUserRow
): { first: string; surplus: number; othersTitle: string | undefined } => {
    const names = (user.memberships ?? [])
        .map((membership) => membership.organizationName)
        .filter((name): name is string => Boolean(name));

    // accountCount is the API's own count, preferred over the array length.
    const total = user.accountCount ?? names.length;
    const surplus = Math.max(0, total - 1);

    return {
        first: names[0] ?? '',
        surplus,
        othersTitle: surplus > 0 ? names.slice(1).join(' · ') || undefined : undefined,
    };
};

/** « plusieurs », the word an aggregate says when there is no single answer. */
export const SEVERAL = () => c('Users aggregate').t`Several`;

/**
 * A null roleName means the memberships diverge (« plusieurs »), never « unknown ». Under a role
 * filter the API switches this field to the role that made the row match, so never re-derive
 * « plusieurs » on top of a non-null value.
 */
export const roleCell = (user: AccountUserRow): { label: string; aggregated: boolean } =>
    user.roleName ? { label: user.roleName, aggregated: false } : { label: SEVERAL(), aggregated: true };

/** Same rule as the role: null means « they diverge », never « unknown ». */
export const statusIsAggregated = (user: AccountUserRow): boolean => !user.status;

/**
 * The role and status filters match a person as soon as one membership qualifies, and the row
 * still lists all of their accounts. Shown only while a filter is active.
 */
export const somewhereNotice = (hasRoleFilter: boolean, hasStatusFilter: boolean): string | null => {
    if (hasRoleFilter && hasStatusFilter) {
        return c('Users filter notice')
            .t`These filters match a person as soon as one of their memberships qualifies. The row still lists all of their accounts, and the Role and Status columns show what made the person match.`;
    }
    if (hasRoleFilter) {
        return c('Users filter notice')
            .t`This filter matches a person who holds that role on at least one account. The row still lists all of their accounts, and the Role column shows the role that made them match.`;
    }
    if (hasStatusFilter) {
        return c('Users filter notice')
            .t`This filter matches a person who is in that state on at least one account. The row still lists all of their accounts, and the Status column shows the state that made them match.`;
    }
    return null;
};
